// Connexion : même design que Safecheck-sinistre. À gauche, la fenêtre ASSURANCES ;
// à droite, le formulaire sur un panneau de verre clair.
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { motion, MotionConfig, type Transition } from "framer-motion";
import { FiAlertCircle, FiArrowLeft, FiArrowRight, FiClock, FiEye, FiEyeOff, FiLock, FiMail, FiShield } from "react-icons/fi";
import { loginRequest } from "../api/companyApi";
import { apiRoleToAccountType, getAccountTypeRoute, storeAuth } from "../auth";
import "./Fenetres.css";
import "./LoginPage.css";

const RESSORT: Transition = { type: "spring", stiffness: 170, damping: 26 };
const RESSORT_LOURD: Transition = { type: "spring", stiffness: 120, damping: 24 };
const COURBE = [0.7, 0, 0.2, 1] as const;

// Entrée en cascade des éléments du formulaire.
const apparition = (rang: number) => ({
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  transition: { ...RESSORT, delay: 0.35 + rang * 0.07 },
});

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const resetSuccess =
    location.state && typeof location.state === "object" && "message" in location.state
      ? String((location.state as { message?: string }).message ?? "")
      : null;
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErreur(null);
    setEnCours(true);

    try {
      const response = await loginRequest({ email: email.trim(), password });
      storeAuth(response.access_token, response.user);
      const accountType = apiRoleToAccountType(response.user.role);
      navigate(getAccountTypeRoute(accountType));
    } catch (err) {
      setErreur(err instanceof Error ? err.message : "Connexion impossible.");
    } finally {
      setEnCours(false);
    }
  };

  return (
    // reducedMotion="user" : animations coupées si le système demande moins de mouvement.
    <MotionConfig reducedMotion="user">
      <div className="sc-login">
        <section className="sc-porte sc-porte-assurances sc-login-visuel" aria-hidden="true">
          <span className="sc-porte-media">
            <span className="sc-porte-zoom">
              <motion.img
                src="/login-assurances.jpg"
                alt=""
                decoding="async"
                fetchPriority="high"
                style={{ objectPosition: "32% 30%" }}
                initial={{ scale: 1.14 }}
                animate={{ scale: [1.14, 1, 1.04] }}
                transition={{ duration: 18, times: [0, 0.1, 1], ease: "easeInOut", repeat: Infinity, repeatType: "mirror" }}
              />
            </span>
            <span className="sc-porte-voile" />
            <motion.span className="sc-porte-rideau" initial={{ scaleY: 1 }} animate={{ scaleY: 0 }} transition={{ duration: 0.8, ease: COURBE, delay: 0.05 }} />
          </span>

          <span className="sc-porte-corps">
            <motion.span className="sc-porte-surtitre" initial={{ opacity: 0, x: -14 }} animate={{ opacity: 1, x: 0 }} transition={{ ...RESSORT, delay: 0.55 }}>
              <span className="sc-porte-pastille">
                <FiShield />
              </span>
              <span className="sc-porte-indice">01</span>
              <span className="sc-porte-filet" />
              Sociétés d&apos;assurance
            </motion.span>
            <span className="sc-porte-masque">
              <motion.span className="sc-porte-titre" initial={{ y: "108%" }} animate={{ y: 0 }} transition={{ ...RESSORT_LOURD, delay: 0.45 }}>
                Assurances
              </motion.span>
            </span>
            <motion.span className="sc-porte-texte" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ ...RESSORT, delay: 0.7 }}>
              Factures d&apos;hôpitaux contrôlées, plafonds suivis, portefeuille en temps réel.
            </motion.span>
            <motion.span className="sc-porte-pied" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, delay: 0.95 }}>
              <motion.span className="sc-porte-ligne" initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 0.9, ease: COURBE, delay: 0.85 }} />
              <span className="sc-porte-mention">Gestion confiée</span>
              <span className="sc-porte-note">Safecheck RDC</span>
            </motion.span>
          </span>
        </section>

        <main id="contenu" className="sc-login-panneau">
          <div className="sc-login-corps">
            <motion.div {...apparition(0)}>
              <Link to="/" className="sc-login-retour">
                <FiArrowLeft aria-hidden="true" /> Accueil
              </Link>
            </motion.div>

            <motion.div {...apparition(1)}>
              <img src="/logo-fonce.png" alt="Safecheck RDC" width={254} height={84} fetchPriority="high" className="sc-logo" />
            </motion.div>

            <motion.h1 className="sc-login-titre" {...apparition(2)}>
              Connexion
            </motion.h1>
            <motion.p className="sc-login-sous" {...apparition(3)}>
              Espace Assurances, gestion confiée.
            </motion.p>

            {resetSuccess ? <p className="sc-login-succes">{resetSuccess}</p> : null}

            <form onSubmit={handleSubmit}>
              <motion.div {...apparition(4)}>
                <label htmlFor="email" className="sc-login-label">
                  Adresse e-mail
                </label>
                <div className="sc-login-champ">
                  <FiMail aria-hidden="true" />
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    autoComplete="username"
                    inputMode="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                </div>
              </motion.div>

              <motion.div {...apparition(5)}>
                <label htmlFor="password" className="sc-login-label">
                  Mot de passe
                </label>
                <div className="sc-login-champ">
                  <FiLock aria-hidden="true" />
                  <input
                    id="password"
                    name="password"
                    type={visible ? "text" : "password"}
                    required
                    autoComplete="current-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                  />
                  <button
                    type="button"
                    className="sc-login-oeil"
                    onClick={() => setVisible((v) => !v)}
                    aria-label={visible ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                    aria-pressed={visible}
                  >
                    {visible ? <FiEyeOff aria-hidden="true" /> : <FiEye aria-hidden="true" />}
                  </button>
                </div>
                <Link to="/login/forgot" className="sc-login-oubli">
                  Mot de passe oublié ?
                </Link>
              </motion.div>

              <motion.div {...apparition(6)}>
                <motion.button type="submit" className="sc-login-bouton" disabled={enCours} whileHover={enCours ? undefined : "survol"} whileTap={{ scale: 0.98 }}>
                  Se connecter
                  <motion.span className="sc-porte-fleche" aria-hidden="true" variants={{ survol: { x: 4 } }} transition={RESSORT}>
                    {enCours ? <span className="sc-login-spinner" /> : <FiArrowRight />}
                  </motion.span>
                </motion.button>
              </motion.div>

              <div aria-live="polite">
                {erreur && (
                  <motion.p className="sc-login-erreur" role="alert" initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} transition={RESSORT}>
                    <FiAlertCircle aria-hidden="true" />
                    <span>{erreur}</span>
                  </motion.p>
                )}
              </div>
            </form>

            <motion.p className="sc-login-pied" {...apparition(7)}>
              <FiClock aria-hidden="true" /> Session fermée après 15 minutes d&apos;inactivité.
            </motion.p>
          </div>
        </main>
      </div>
    </MotionConfig>
  );
}
